/**
 * Decode the message to show for a failed Quality Check call.
 *
 * There are two error shapes to decode, not one, and the difference is what an earlier
 * version of this got wrong. `services/qualityCheck` throws the server's own `response.data`
 * when a 2xx arrives that is not 200 — a bare `{ errorMessage }`. But an HTTP failure never
 * reaches that line: axios rejects first, with an `AxiosError` whose `message` is its own
 * English string ("Request failed with status code 403") and whose `response.data` holds what
 * the server actually said. Reading only the top level therefore missed the server's words on
 * every real failure and printed axios's instead.
 *
 * So `response.data` is read first, and the raw throw second.
 *
 * The module sits beside the service rather than inside it for a practical reason: every
 * Quality Check test mocks `qualityCheck` wholesale, so a pure helper exported from there
 * vanishes under the mock and every call site dies with `qcErrorMessage is not a function`.
 * Decoding an error is not a service call and does not want to be stubbed with one.
 */
interface QcErrorBody {
    errorMessage?: unknown;
    message?: unknown;
}

/**
 * The server's own words out of a response body, where both keys are the server's.
 */
function wordsFromBody(candidate: unknown): string | null {
    if (!candidate || typeof candidate !== 'object') return null;
    const body = candidate as QcErrorBody;
    if (typeof body.errorMessage === 'string' && body.errorMessage) return body.errorMessage;
    if (typeof body.message === 'string' && body.message) return body.message;
    return null;
}

/**
 * The server's words out of the raw throw — `errorMessage` only.
 *
 * `message` is deliberately not read here. On the raw error that key belongs to axios or to
 * `Error`, not to the server: it holds "Request failed with status code 403" or "Network
 * Error". See `transportMessage` for where it is still used, and why.
 */
function wordsFromThrow(candidate: unknown): string | null {
    if (!candidate || typeof candidate !== 'object') return null;
    const body = candidate as QcErrorBody;
    return typeof body.errorMessage === 'string' && body.errorMessage ? body.errorMessage : null;
}

/** Whatever axios or `Error` put on `message`. Not the server's words, but not nothing. */
function transportMessage(candidate: unknown): string | null {
    if (!candidate || typeof candidate !== 'object') return null;
    const body = candidate as QcErrorBody;
    return typeof body.message === 'string' && body.message ? body.message : null;
}

function responseBody(err: unknown): unknown {
    if (!err || typeof err !== 'object') return undefined;
    const response = (err as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return undefined;
    return (response as { data?: unknown }).data;
}

/**
 * `fallback` is what to say when the server sent nothing usable.
 *
 * Pass `null` when the surrounding UI already states the failure and a generic English
 * sentence would only repeat it in the wrong language. That overload now returns null there,
 * where it used to fall through to axios's own `message` — so the one call site written for
 * it reliably got "Request failed with status code 403" instead of nothing.
 *
 * A string fallback behaves the other way on purpose: axios's sentence is preferred over it,
 * because it carries the status code and the caller's fallback does not.
 */
export function qcErrorMessage(err: unknown, fallback: string): string;
export function qcErrorMessage(err: unknown, fallback: null): string | null;
export function qcErrorMessage(err: unknown, fallback: string | null): string | null {
    const fromServer = wordsFromBody(responseBody(err)) ?? wordsFromThrow(err);
    if (fromServer) return fromServer;
    // Asking for null means "say nothing rather than the wrong thing", so axios's English is
    // not an acceptable stand-in. Offering a generic fallback means the opposite: axios's
    // sentence is more specific than ours — it carries the status code — so it wins.
    if (fallback === null) return null;
    return transportMessage(err) ?? fallback;
}
